/** One choice question a changeset raises (plan §1.4 item 4) — rendered in the ledger
 *  AND inline under the field that raised it (§1.3 "Needs a choice"); both instances
 *  answer the same editor state (a "… now" confirmation passes `onAnswer` to keep its
 *  own). A required question without an answer says so in words next to its icon; a
 *  pre-answered one is marked as the default. An answer's `warning` (what choosing it
 *  means) is shown under it whether picked or not; an answer the operator may not give is
 *  disabled with the reason, never hidden (the order ref's "update the linked bookings"
 *  needs console_bookings_edit). An option whose preview reason is the question
 *  (`reasonIsQuestion`: "Re-key import matching to FH-2? …") is asked in those words once
 *  the preview is in; any other by its label. */
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  alpha,
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useId } from 'react';

import type { RaisedChoice } from '../editor/choices';
import { useRecordEditor } from '../editor/context';

export function ChoiceQuestion({
  choice,
  onAnswer,
}: {
  choice: RaisedChoice;
  /** Answer elsewhere than the editing session (a "… now" confirmation keeps its own). */
  onAnswer?: (key: string, value: string | boolean) => void;
}) {
  const editor = useRecordEditor();
  const setAnswer = onAnswer ?? editor.setAnswer;
  const { option, answer, defaulted, reason } = choice;
  const uid = useId();
  const questionId = `${uid}-q`;
  const unanswered = option.required && answer === null;

  return (
    <Box
      role="group"
      aria-labelledby={questionId}
      data-choice={option.key}
      data-unanswered={unanswered || undefined}
      sx={{
        border: 1,
        borderColor: (t) => (unanswered ? alpha(t.palette.warning.main, 0.6) : t.palette.divider),
        bgcolor: (t) => (unanswered ? alpha(t.palette.warning.main, 0.06) : 'transparent'),
        borderRadius: 1,
        px: 1.25,
        py: 1,
        display: 'grid',
        gap: 0.25,
      }}
    >
      {/* The icon keeps to the first line; a long question wraps beside it. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 0.75, alignItems: 'start' }}>
        <AltRouteIcon
          aria-hidden
          sx={{ fontSize: 16, mt: '2px', color: unanswered ? 'warning.main' : 'text.secondary' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'baseline', columnGap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
          <Typography id={questionId} variant="body2" sx={{ fontWeight: 600 }}>
            {(option.reasonIsQuestion && reason) || option.label}
          </Typography>
          {unanswered ? (
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Answer to save
            </Typography>
          ) : defaulted ? (
            <Typography variant="caption" color="text.secondary">
              Default
            </Typography>
          ) : null}
        </Box>
      </Box>
      {option.help && (
        <Typography variant="caption" color="text.secondary">
          {option.help}
        </Typography>
      )}
      {option.type === 'boolean' ? (
        <FormControlLabel
          sx={{ ml: -0.75 }}
          control={
            <Checkbox
              size="small"
              checked={answer === true}
              onChange={(e) => setAnswer(option.key, e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              {option.values.find((v) => v.value === true)?.label ?? 'Yes'}
            </Typography>
          }
        />
      ) : (
        <RadioGroup
          name={uid}
          value={answer === null ? '' : String(answer)}
          onChange={(e) => {
            const picked = option.values.find((v) => String(v.value) === e.target.value && !v.disabled);
            if (picked) setAnswer(option.key, picked.value);
          }}
        >
          {option.values.map((v) => {
            const notes = Boolean(v.warning) || (v.disabled && Boolean(v.disabledReason));
            return (
              <FormControlLabel
                key={String(v.value)}
                value={String(v.value)}
                disabled={v.disabled}
                data-choice-value={String(v.value)}
                sx={{ ml: -0.75, ...(notes ? { alignItems: 'flex-start' } : {}) }}
                control={<Radio size="small" {...(notes ? { sx: { pt: 0.25 } } : {})} />}
                label={
                  notes ? (
                    <Box sx={{ display: 'grid', gap: 0.25, py: 0.5 }}>
                      <Typography variant="body2">{v.label}</Typography>
                      {/* What each answer does stays readable before it is picked (D10). */}
                      {v.warning && (
                        <Typography variant="caption" color="text.secondary" data-choice-warning>
                          {v.warning}
                        </Typography>
                      )}
                      {v.disabled && v.disabledReason && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          data-choice-disabled
                          sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
                        >
                          <LockOutlinedIcon aria-hidden sx={{ fontSize: 14, mt: '2px', flex: 'none' }} />
                          <span>{v.disabledReason}</span>
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2">{v.label}</Typography>
                  )
                }
              />
            );
          })}
        </RadioGroup>
      )}
    </Box>
  );
}
